import { BaseChartProps } from "@/shared/types/charts";
import { useTheme } from "@mui/material";
import { BarSeriesOption, EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";

type BarChartProps = BaseChartProps & {
  series: BarSeriesOption[];
  axisLabels: string[];
  stacked?: boolean;
};

export const BarChart = ({
  axisLabels,
  series,
  xAxisLabelFormatter,
  yAxisLabel,
  yAxisLabelFormatter,
  tooltipOptions,
  stacked = false,
  overrides = {},
}: BarChartProps) => {
  const theme = useTheme();
  const tooltipPalette = (theme as any).palette?.echartTooltip ?? {};
  const textColorPalette = (theme as any).palette?.brandedTypography ?? {};
  const guideLinesPalette = (theme as any).palette?.ChartGuideLines ?? {};

  const baseOption: EChartsOption = {
    grid: {
      left: yAxisLabel ? 20 : 4,
      right: 20,
      top: 40,
      bottom: 20,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      padding: 0,
      borderWidth: 0,
      borderColor: "transparent",
      axisPointer: {
        type: "shadow",
      },
      backgroundColor: tooltipPalette.background || theme.palette.background?.paper || "#fff",
      textStyle: {
        color: tooltipPalette.textColor || theme.palette.text?.primary,
      },
      ...(tooltipOptions ?? {}),
    },
    xAxis: {
      data: axisLabels,
      axisLabel: {
        color: textColorPalette.textColor || theme.palette.text?.primary,
        fontSize: 14,
        formatter: xAxisLabelFormatter,
        padding: [12, 0, 0, 0],
      },
    },
    yAxis: {
      type: "value",
      ...(yAxisLabel && {
        name: yAxisLabel,
        nameTextStyle: {
          color: textColorPalette.textColor || theme.palette.text?.primary,
          fontWeight: 600,
          fontSize: "14px",
          padding: [0, 0, 10, -40],
        },
      }),
      axisLabel: {
        fontSize: 14,
        color: textColorPalette.textColor || theme.palette.text?.primary,
        formatter: yAxisLabelFormatter,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: guideLinesPalette.color || theme.palette.divider,
        },
      },
    },
    series: series.map((data) => ({
      ...data,
      type: "bar",
      color: data.color,
      barMaxWidth: 24,
      ...(stacked && {
        stack: "total",
      }),
    })),
  };

  const option: EChartsOption = {
    ...baseOption,
    ...overrides,
  };

  return <ReactECharts option={option} style={{ minHeight: "500px" }} notMerge={true} />;
};
